// src/pages/ProjectDetailPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Tabs, Tab, Button,
  IconButton, CircularProgress, Alert, TextField,
  MenuItem, FormControl, InputLabel, Select, Chip, Stack,
  LinearProgress, Tooltip,
  ToggleButton, ToggleButtonGroup, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTheme } from '@mui/material/styles';

// --- Iconos de MUI ---
import {
  ArrowBack, CheckCircle, AttachFile, Assignment, Save, Flag, Business,
  Description,
  Wallet, ReceiptLong, AttachMoney, ChecklistRtl, Edit, Delete,
  ExpandMore, ExpandLess // ✅ Iconos
} from '@mui/icons-material';

// --- Iconos Modernos (LUCIDE) ---
import { 
  FileText, Target, Building2, User
} from 'lucide-react';

import TaskBoard from './TaskBoard';
import projectService from '../services/projectService';
import invoiceService from '../services/invoiceService';
import userService from '../services/userService';
import { openStoredFile } from '../services/fileAccessService';
import { downloadBlobFile, openBlobFile } from '../services/nativeFileService';
import ProjectFiles from '../components/ProjectFiles'; 

// --- CONSTANTES ---
const STATUS_LABELS = {
  planning: 'Planning', in_progress: 'In Progress', on_hold: 'On Hold',
  completed: 'Completed', cancelled: 'Cancelled',
};

const PRIORITY_LABELS = {
  low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent',
};

const EXPENSE_CATEGORIES = {
  'Office Supplies': '#60a5fa',
  'Rent': '#f472b6',
  'Utilities': '#fbbf24',
  'Salaries': '#34d399',
  'Software': '#a78bfa',
  'Travel': '#fb7185',
  'Marketing': '#f87171',
  'Other': '#94a3b8'
};

// --- ESTILOS ---
const glassCardStyle = {
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  bgcolor: 'rgba(30, 41, 59, 0.7)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(148, 163, 184, 0.1)',
  borderRadius: { xs: 2.5, md: 4 },
  p: { xs: 1.5, sm: 2, md: 3 },
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  height: '100%'
};

const iconBoxStyle = (colorBg, colorText) => ({
  p: 1,
  borderRadius: 2,
  bgcolor: colorBg,
  color: colorText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mr: { xs: 1, sm: 2 },
  flexShrink: 0
});

const mobileSectionStyle = {
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  p: { xs: 1.25, sm: 2, md: 4 },
  borderRadius: { xs: 2.5, md: 4 },
  minHeight: { xs: 0, md: 600 },
  '& .MuiGrid-item': { minWidth: 0 }
};

const scrollTableStyle = {
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch'
};

const detailRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 1.5,
  minWidth: 0,
  '& > *': { minWidth: 0, overflowWrap: 'anywhere' },
  '& > *:last-child': { textAlign: 'right' }
};

const DEFAULT_FOLLOWUP_STATUS = 'done';
const FOLLOWUP_STATUSES = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'holding', label: 'Holding' },
  { value: 'done', label: 'Done' },
  { value: 'blocked', label: 'Blocked' }
];

const FOLLOWUP_QUICK_TEMPLATES = [
  'Shared project update',
  'Reviewed open items',
  'Waiting for client response',
  'Coordinated internal next step',
  'Sent deliverable update',
  'Pending approval'
];

const FOLLOWUP_STATUS_META = {
  planned: { label: 'Planned', color: '#93c5fd', bg: 'rgba(59,130,246,0.16)' },
  in_progress: { label: 'In Progress', color: '#fbbf24', bg: 'rgba(245,158,11,0.16)' },
  holding: { label: 'Holding', color: '#d8b4fe', bg: 'rgba(168,85,247,0.18)' },
  done: { label: 'Done', color: '#86efac', bg: 'rgba(34,197,94,0.16)' },
  blocked: { label: 'Blocked', color: '#fca5a5', bg: 'rgba(239,68,68,0.16)' }
};

const getDateInputValue = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getDateDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return getDateInputValue(date);
};

const getFollowupStatusMeta = (statusRaw) => {
  const key = String(statusRaw || '').toLowerCase().trim();
  return FOLLOWUP_STATUS_META[key] || {
    label: key || 'Unknown',
    color: '#cbd5e1',
    bg: 'rgba(148,163,184,0.16)'
  };
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLightMode = theme.palette.mode === 'light';
  const followupPalette = isLightMode
    ? {
      pageBg: '#f8fafc',
      text: '#0f172a',
      subText: '#475569',
      mainBorder: '1px solid rgba(15,23,42,0.12)',
      panelBg: 'linear-gradient(120deg, rgba(224,242,254,0.92) 0%, rgba(219,234,254,0.92) 45%, rgba(248,250,252,0.96) 100%)',
      cardBg: 'linear-gradient(170deg, rgba(255,255,255,0.95) 0%, rgba(241,245,249,0.96) 100%)',
      cardBorder: '1px solid rgba(59,130,246,0.16)',
      tableBg: 'rgba(255,255,255,0.9)',
      tableBorder: '1px solid rgba(148,163,184,0.3)'
    }
    : {
      pageBg: '#0f172a',
      text: '#e2e8f0',
      subText: '#cbd5e1',
      mainBorder: '1px solid rgba(56, 189, 248, 0.25)',
      panelBg: 'linear-gradient(120deg, rgba(14,116,144,0.26) 0%, rgba(29,78,216,0.18) 50%, rgba(15,23,42,0.22) 100%)',
      cardBg: 'linear-gradient(170deg, rgba(30,41,59,0.88) 0%, rgba(15,23,42,0.92) 100%)',
      cardBorder: '1px solid rgba(148,163,184,0.2)',
      tableBg: 'rgba(15,23,42,0.38)',
      tableBorder: '1px solid rgba(148,163,184,0.2)'
    };
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTab, setCurrentTab] = useState(0); 
  const [isNewProject, setIsNewProject] = useState(false);
  
  // ✅ Estado para gastos
  const [expenses, setExpenses] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [linkedDocuments, setLinkedDocuments] = useState([]);
  const [workspacePermissions, setWorkspacePermissions] = useState({
    tasks: false,
    finance: false,
    billingDocuments: false,
    documents: false
  });
  const [workspaceSummary, setWorkspaceSummary] = useState({
    tasks_total: 0,
    tasks_done: 0,
    tasks_open: 0,
    project_budget_total: 0,
    project_budget_source: 'project_field',
    current_po_id: null,
    current_po_number: null,
    current_po_status: null,
    current_po_total: 0,
    expenses_total: 0,
    invoices_total: 0,
    billing_documents_total: 0,
    profit_total: 0,
    billed_total: 0,
    paid_total: 0,
    pending_billing_total: 0,
    documents_total: 0
  });
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [followupsError, setFollowupsError] = useState('');
  const [followupsData, setFollowupsData] = useState({
    scope: 'own',
    summary: { total_entries: 0, users_count: 0, group_by: 'day' },
    groups: [],
    items: []
  });
  const [followupAssignableUsers, setFollowupAssignableUsers] = useState([]);
  const [followupFilters, setFollowupFilters] = useState({
    group_by: 'day',
    from: getDateDaysAgo(14),
    to: getDateInputValue(new Date()),
    user_id: 'all'
  });
  const [followupForm, setFollowupForm] = useState({
    entry_date: getDateInputValue(new Date()),
    title: '',
    details: '',
    next_step: '',
    status: DEFAULT_FOLLOWUP_STATUS,
    assigned_to_user_id: ''
  });
  const [followupSaving, setFollowupSaving] = useState(false);
  const [editingFollowupId, setEditingFollowupId] = useState(null);
  const [showFollowupAdvancedFields, setShowFollowupAdvancedFields] = useState(false);

  const [formData, setFormData] = useState({
    name: '', description: '', client_id: '', status: 'planning',
    priority: 'medium', start_date: '', end_date: '', budget: '',
    company_name: '', business_units: []
  });

  const loadFollowups = useCallback(async (customFilters = {}, projectIdOverride = projectId) => {
    if (!projectIdOverride || projectIdOverride === 'new') return;
    try {
      setFollowupsLoading(true);
      setFollowupsError('');
      const finalFilters = { ...followupFilters, ...customFilters };
      const response = await projectService.getProjectFollowups(projectIdOverride, finalFilters);
      if (response?.success) {
        setFollowupsData({
          scope: response.scope || 'own',
          summary: response.summary || { total_entries: 0, users_count: 0, group_by: finalFilters.group_by || 'day' },
          groups: Array.isArray(response.groups) ? response.groups : [],
          items: Array.isArray(response.items) ? response.items : []
        });
      }
    } catch (err) {
      setFollowupsError(err.response?.data?.message || 'The project follow-up could not be loaded.');
    } finally {
      setFollowupsLoading(false);
    }
  }, [followupFilters, projectId]);

  // ✅ Función de carga unificada
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      try {
        const usersResponse = await userService.getUsers();
        setFollowupAssignableUsers(Array.isArray(usersResponse?.users) ? usersResponse.users : []);
      } catch (usersErr) {
        setFollowupAssignableUsers([]);
      }

      // 1) Intentar cargar vista unificada (workspace)
      try {
        const workspaceResponse = await projectService.getProjectWorkspace(projectId);
        if (workspaceResponse?.success) {
          setProject(workspaceResponse.project || null);
          setExpenses(workspaceResponse.related?.expenses || []);
          setLinkedInvoices(workspaceResponse.related?.invoices || []);
          setLinkedDocuments(workspaceResponse.related?.documents || []);
          setWorkspacePermissions({
            tasks: Boolean(workspaceResponse.permissions?.tasks),
            finance: Boolean(workspaceResponse.permissions?.finance),
            billingDocuments: Boolean(workspaceResponse.permissions?.billingDocuments),
            documents: Boolean(workspaceResponse.permissions?.documents)
          });
          setWorkspaceSummary({
            tasks_total: Number(workspaceResponse.summary?.tasks_total || 0),
            tasks_done: Number(workspaceResponse.summary?.tasks_done || 0),
            tasks_open: Number(workspaceResponse.summary?.tasks_open || 0),
            project_budget_total: Number(workspaceResponse.summary?.project_budget_total || 0),
            project_budget_source: workspaceResponse.summary?.project_budget_source || 'project_field',
            current_po_id: workspaceResponse.summary?.current_po_id || null,
            current_po_number: workspaceResponse.summary?.current_po_number || null,
            current_po_status: workspaceResponse.summary?.current_po_status || null,
            current_po_total: Number(workspaceResponse.summary?.current_po_total || 0),
            expenses_total: Number(workspaceResponse.summary?.expenses_total || 0),
            invoices_total: Number(workspaceResponse.summary?.invoices_total || 0),
            billing_documents_total: Number(workspaceResponse.summary?.billing_documents_total || workspaceResponse.summary?.invoices_total || 0),
            profit_total: Number(workspaceResponse.summary?.profit_total || 0),
            billed_total: Number(workspaceResponse.summary?.billed_total || 0),
            paid_total: Number(workspaceResponse.summary?.paid_total || 0),
            pending_billing_total: Number(workspaceResponse.summary?.pending_billing_total || 0),
            documents_total: Number(workspaceResponse.summary?.documents_total || 0)
          });
          setError('');
          return;
        }
      } catch (workspaceErr) {
        // Si el endpoint no existe aún en otro entorno, hacemos fallback a la lógica vieja.
        if (workspaceErr?.response?.status && workspaceErr.response.status !== 404) {
          throw workspaceErr;
        }
      }

      // 2) Fallback legado
      const response = await projectService.getProjectById(projectId);
      setProject(response.project);
      setLinkedInvoices([]);
      setLinkedDocuments([]);
      setWorkspacePermissions({
        tasks: true,
        finance: true,
        billingDocuments: true,
        documents: true
      });
      setWorkspaceSummary({
        tasks_total: 0,
        tasks_done: 0,
        tasks_open: 0,
        project_budget_total: 0,
        project_budget_source: 'project_field',
        current_po_id: null,
        current_po_number: null,
        current_po_status: null,
        current_po_total: 0,
        expenses_total: 0,
        invoices_total: 0,
        billing_documents_total: 0,
        profit_total: 0,
        billed_total: 0,
        paid_total: 0,
        pending_billing_total: 0,
        documents_total: 0
      });
      if (projectId !== 'new') {
        const expRes = await invoiceService.getExpenses();
        if (expRes.success) {
          const projExpenses = (expRes.expenses || []).filter((entry) => entry.project_id === parseInt(projectId, 10));
          setExpenses(projExpenses);
          setWorkspaceSummary((prev) => ({
            ...prev,
            expenses_total: projExpenses.reduce((acc, entry) => acc + (parseFloat(entry.amount) || 0), 0)
          }));
        }
      }

      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Error loading project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId === 'new') {
      setIsNewProject(true);
      setLoading(false);
      return;
    }
    setIsNewProject(false);
    loadData();
  }, [projectId, loadData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateProject = async () => {
    try {
      setLoading(true);
      const response = await projectService.createProject(formData);
      navigate(`/projects/${response.project.id}`);
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.response?.data?.message || 'Error creating project');
      setLoading(false);
    }
  };

  // ✅ CÁLCULOS FINANCIEROS
  const financeStats = useMemo(() => {
    if (!project) return { budget: 0, billed: 0, spent: 0, profit: 0, paid: 0, remaining: 0, percent: 0 };
    
    const budget = Number(workspaceSummary.project_budget_total || parseFloat(project.budget) || 0);
    const billed = Number(workspaceSummary.billed_total || 0);
    const profit = Number(workspaceSummary.profit_total || 0);
    const paid = Number(workspaceSummary.paid_total || 0);
    const remaining = budget - billed;
    const spent = remaining;
    const percent = budget > 0 ? (billed / budget) * 100 : 0;

    return { budget, billed, spent, profit, paid, remaining, percent: Math.min(percent, 100) };
  }, [project, workspaceSummary]);

  const generalProjectFiles = useMemo(() => (
    Array.isArray(project?.attachments)
      ? project.attachments.filter((file) => String(file?.category || 'general').toLowerCase() !== 'intercompany_invoice')
      : []
  ), [project?.attachments]);

  const intercompanyInvoiceFiles = useMemo(() => (
    Array.isArray(project?.attachments)
      ? project.attachments.filter((file) => String(file?.category || '').toLowerCase() === 'intercompany_invoice')
      : []
  ), [project?.attachments]);

  const progressInfo = useMemo(() => {
    if (!project) return { value: 0, color: 'primary' };
    switch (project.status) {
      case 'planning': return { value: 10, color: 'info' };
      case 'in_progress': return { value: 50, color: 'primary' };
      case 'completed': return { value: 100, color: 'success' };
      case 'on_hold': return { value: 100, color: 'warning', isPaused: true };
      case 'cancelled': return { value: 100, color: 'error' };
      default: return { value: 0, color: 'primary' };
    }
  }, [project]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not defined';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const resetFollowupForm = () => {
    setEditingFollowupId(null);
    setShowFollowupAdvancedFields(false);
    setFollowupForm({
      entry_date: getDateInputValue(new Date()),
      title: '',
      details: '',
      next_step: '',
      status: DEFAULT_FOLLOWUP_STATUS,
      assigned_to_user_id: ''
    });
  };

  const handleFollowupFilterChange = async (field, value) => {
    const nextFilters = { ...followupFilters, [field]: value };
    setFollowupFilters(nextFilters);
    await loadFollowups(nextFilters);
  };

  const handleFollowupSave = async () => {
    const trimmedTitle = String(followupForm.title || '').trim();
    if (!trimmedTitle) {
      setFollowupsError('Enter a short title for the completed activity.');
      return;
    }
    try {
      setFollowupSaving(true);
      setFollowupsError('');
      const payload = {
        ...followupForm,
        assigned_to_user_id: followupForm.assigned_to_user_id || null
      };
      if (editingFollowupId) {
        await projectService.updateProjectFollowup(projectId, editingFollowupId, payload);
      } else {
        await projectService.createProjectFollowup(projectId, payload);
      }
      resetFollowupForm();
      await loadFollowups();
    } catch (err) {
      setFollowupsError(err.response?.data?.message || 'The follow-up could not be saved.');
    } finally {
      setFollowupSaving(false);
    }
  };

  const handleFollowupEdit = (item) => {
    setEditingFollowupId(item.id);
    setShowFollowupAdvancedFields(true);
    setFollowupForm({
      entry_date: getDateInputValue(item.entry_date),
      title: item.title || '',
      details: item.details || '',
      next_step: item.next_step || '',
      status: item.status || DEFAULT_FOLLOWUP_STATUS,
      assigned_to_user_id: item.assigned_to_user_id ? String(item.assigned_to_user_id) : ''
    });
  };

  const handleFollowupDelete = async (item) => {
    const confirmed = window.confirm(`Delete follow-up "${item.title}"?`);
    if (!confirmed) return;
    try {
      setFollowupsError('');
      await projectService.deleteProjectFollowup(projectId, item.id);
      if (editingFollowupId === item.id) resetFollowupForm();
      await loadFollowups();
    } catch (err) {
      setFollowupsError(err.response?.data?.message || 'The follow-up could not be deleted.');
    }
  };

  const followupUserSummary = useMemo(() => {
    const rows = [];
    const map = new Map();
    const items = Array.isArray(followupsData.items) ? followupsData.items : [];

    for (const item of items) {
      const key = item.user_id || `u-${item.user_name || 'unknown'}`;
      if (!map.has(key)) {
        map.set(key, {
          user_id: item.user_id || null,
          user_name: item.user_name || 'Unknown user',
          user_email: item.user_email || '',
          entries: 0,
          assigned_count: 0,
          done_count: 0,
          blocked_count: 0,
          latest_entry_date: item.entry_date || null
        });
      }
      const bucket = map.get(key);
      bucket.entries += 1;
      if (item.assigned_to_user_id) bucket.assigned_count += 1;
      if (String(item.status || '').toLowerCase() === 'done') bucket.done_count += 1;
      if (String(item.status || '').toLowerCase() === 'blocked') bucket.blocked_count += 1;
      if (!bucket.latest_entry_date || String(item.entry_date || '') > String(bucket.latest_entry_date || '')) {
        bucket.latest_entry_date = item.entry_date || bucket.latest_entry_date;
      }
    }

    map.forEach((value) => {
      rows.push({ ...value });
    });

    rows.sort((a, b) => {
      if (b.entries !== a.entries) return b.entries - a.entries;
      return b.assigned_count - a.assigned_count;
    });
    return rows;
  }, [followupsData.items]);

  const handleExportFollowupsExcel = async () => {
    try {
      setFollowupsError('');
      const blob = await projectService.exportProjectFollowupsExcel(projectId, {
        from: followupFilters.from,
        to: followupFilters.to,
        user_id: followupFilters.user_id
      });
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const safeName = String(project?.name || `project_${projectId}`).replace(/[\\/:*?"<>|]+/g, '_');
      const fileName = `followup_${safeName}_${yyyy}${mm}${dd}.xlsx`;

      await downloadBlobFile(blob, {
        fileName,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    } catch (error) {
      console.error('Error exporting followups excel:', error);
      setFollowupsError(error.response?.data?.message || 'The follow-up Excel file could not be exported.');
    }
  };

  const handleOpenStoredPath = async (rawPath) => {
    try {
      await openStoredFile(rawPath, 'inline');
    } catch (openError) {
      setError(openError.response?.data?.message || openError.message || 'Unable to open the file.');
    }
  };

  const handleOpenOfficialInvoicePdf = async (invoiceId) => {
    try {
      const blob = await invoiceService.downloadInvoicePDF(invoiceId);
      await openBlobFile(blob, {
        fileName: `invoice-${invoiceId}.pdf`,
        mimeType: 'application/pdf'
      });
    } catch (error) {
      console.error('Error opening official invoice PDF:', error);
      window.alert('The official PDF for this document could not be opened.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  // 1️⃣ MODO CREACIÓN (Sin cambios)
  if (isNewProject) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* ... (Tu formulario de creación existente) ... */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <IconButton onClick={() => navigate('/projects')} sx={{ mr: 2 }}><ArrowBack /></IconButton>
            <Typography variant="h5" fontWeight={700}>Create New Project</Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Project Name" name="name" value={formData.name} onChange={handleInputChange} required variant="outlined" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Company / Client" name="company_name" value={formData.company_name} onChange={handleInputChange} required variant="outlined" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={formData.status} onChange={handleInputChange} label="Status">
                  {Object.entries(STATUS_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select name="priority" value={formData.priority} onChange={handleInputChange} label="Priority">
                  {Object.entries(PRIORITY_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Budget" name="budget" value={formData.budget} onChange={handleInputChange} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Description" name="description" value={formData.description} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Start Date" name="start_date" value={formData.start_date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="End Date" name="end_date" value={formData.end_date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
          <Box display="flex" gap={2} justifyContent="flex-end" mt={4}>
            <Button variant="outlined" onClick={() => navigate('/projects')}>Cancel</Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleCreateProject} disabled={!formData.name}>Create Project</Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // 2️⃣ MODO VISTA
  if (error || !project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Project not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/projects')} sx={{ mt: 2 }} variant="contained">Back</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      overflowX: 'hidden',
      px: { xs: 1.25, sm: 2, md: 3 },
      mt: { xs: 1.25, md: 3 },
      mb: { xs: 2, md: 4 }
    }}>
      {/* --- HEADER --- */}
      <Paper elevation={0} sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden', p: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, md: 3 }, border: '1px solid #e0e0e0', borderRadius: { xs: 2.5, md: 2 }, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white', '& .MuiGrid-item': { minWidth: 0 } }}>
        <Grid container alignItems="flex-start" spacing={2}>
          
          {/* COLUMNA IZQUIERDA: Info General */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minWidth: 0 }}>
              <IconButton aria-label="Back to projects" onClick={() => navigate('/projects')} sx={{ mr: 0.75, color: 'white', flexShrink: 0 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" fontWeight={800} color="inherit" sx={{ minWidth: 0, fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2.125rem' }, overflowWrap: 'anywhere' }}>
                {project.name}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1} useFlexGap alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
              <Chip label={STATUS_LABELS[project.status]} size="small" sx={{ fontWeight: 'bold', bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              <Chip icon={<Flag fontSize="small" style={{color:'white'}} />} label={`Priority: ${PRIORITY_LABELS[project.priority]}`} variant="outlined" size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} />
              {project.company_name && <Chip icon={<Business fontSize="small" style={{color:'white'}} />} label={project.company_name} variant="outlined" size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} />}
              {project.intercompany_sale_enabled && (
                <Chip
                  label="SMTLINK LLC -> SMTLINK SA de CV"
                  variant="outlined"
                  size="small"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontWeight: 700 }}
                />
              )}
            </Stack>
          </Grid>

          {/* COLUMNA DERECHA: MINI DASHBOARD FINANCIERO */}
          <Grid item xs={12} md={5}>
            <Box sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 2,
                p: { xs: 1.25, sm: 2 },
                mb: 2,
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: { xs: 1.25, sm: 2 },
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(255,255,255,0.1)',
                minWidth: 0
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Wallet style={{ fontSize: 14 }} /> BUDGET
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.98rem', sm: '1.25rem' }, overflowWrap: 'anywhere' }}>
                        {formatCurrency(financeStats.budget)}
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ReceiptLong style={{ fontSize: 14 }} /> BILLED
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#86efac', fontSize: { xs: '0.98rem', sm: '1.25rem' }, overflowWrap: 'anywhere' }}>
                        {formatCurrency(financeStats.billed)}
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' }, pt: { xs: 1.25, sm: 0 }, borderTop: { xs: '1px solid rgba(255,255,255,0.18)', sm: 0 } }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>BALANCE</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: financeStats.remaining >= 0 ? '#60a5fa' : '#fda4af', fontSize: { xs: '0.98rem', sm: '1.25rem' }, overflowWrap: 'anywhere' }}>
                        {formatCurrency(financeStats.remaining)}
                    </Typography>
                </Box>
            </Box>

            {/* Progreso */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, minWidth: 0 }}>
               <Box sx={{ textAlign: 'right', width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 180 } }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Status</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progressInfo.value} 
                      sx={{ flex: 1, minWidth: 80, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
                    />
                    <Typography variant="body2" fontWeight={700}>{progressInfo.value}%</Typography>
                  </Box>
               </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 2, md: 3 }, mx: { xs: -1.5, sm: -2, md: -3 }, mb: { xs: -1.5, sm: -2, md: -3 }, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0, minWidth: 0, overflow: 'hidden' }}>
          <Tabs
            value={currentTab} 
            onChange={(e, v) => setCurrentTab(v)} 
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
                maxWidth: '100%',
                minWidth: 0,
                px: { xs: 0.5, sm: 1.5 },
                '& .MuiTabs-scrollButtons': { color: 'white', width: { xs: 32, sm: 40 } },
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', minHeight: 48, minWidth: { xs: 'auto', sm: 120 }, px: { xs: 1.25, sm: 2 }, fontSize: { xs: '0.78rem', sm: '0.875rem' } },
                '& .Mui-selected': { color: 'white !important', fontWeight: 700 },
                '& .MuiTabs-indicator': { bgcolor: 'white' }
            }}
          >
            <Tab
              icon={<CheckCircle sx={{mr:1}}/>}
              iconPosition="start"
              label={`Pendientes${workspaceSummary.tasks_total ? ` (${workspaceSummary.tasks_total})` : ''}`}
              disabled={!workspacePermissions.tasks}
            />
            <Tab icon={<Assignment sx={{mr:1}}/>} iconPosition="start" label="General Info" />
            {/* ✅ NUEVA PESTAÑA DE FINANZAS */}
            <Tab
              icon={<AttachMoney sx={{mr:1}}/>}
              iconPosition="start"
              label={`Finance${workspaceSummary.billing_documents_total ? ` (${workspaceSummary.billing_documents_total})` : ''}`}
              disabled={!workspacePermissions.finance && !workspacePermissions.billingDocuments}
            />
            <Tab
              icon={<AttachFile sx={{mr:1}}/>}
              iconPosition="start"
              label={`Files (${generalProjectFiles.length + linkedDocuments.length})`}
            />
          </Tabs>
        </Box>
      </Paper>

      {/* --- CONTENIDO DE PESTAÑAS --- */}

      {/* 🔹 PESTAÑA 0: TAREAS */}
      {currentTab === 0 && (
        workspacePermissions.tasks
          ? <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}><TaskBoard projectId={projectId} /></Box>
          : (
            <Alert severity="info">
              Tu rol actual no tiene permiso para visualizar tareas de este proyecto.
            </Alert>
          )
      )}

      {/* 🔹 PESTAÑA 1: INFO GENERAL */}
      {currentTab === 1 && (
        <Box sx={{ ...mobileSectionStyle, bgcolor: '#0f172a', color: '#e2e8f0' }}>
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {/* COLUMNA IZQUIERDA */}
            <Grid item xs={12} lg={8}>
              <Stack spacing={{ xs: 1.5, md: 3 }}>
                <Box sx={glassCardStyle}>
                  <Box display="flex" alignItems="center" mb={2} pb={2} borderBottom="1px solid rgba(148, 163, 184, 0.2)">
                    <Box sx={iconBoxStyle('rgba(59, 130, 246, 0.15)', '#60a5fa')}><FileText size={20} /></Box>
                    <Typography variant="h6" fontWeight={600} color="#f8fafc">Project Description</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{project.description || "No description provided."}</Typography>
                </Box>
                
                <Box sx={glassCardStyle}>
                  <Box display="flex" alignItems="center" mb={2} pb={2} borderBottom="1px solid rgba(148, 163, 184, 0.2)">
                    <Box sx={iconBoxStyle('rgba(99, 102, 241, 0.15)', '#818cf8')}><Target size={20} /></Box>
                    <Typography variant="h6" fontWeight={600} color="#f8fafc">Requirements</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#cbd5e1', overflowWrap: 'anywhere' }}>{project.requirements || "No requirements specified."}</Typography>
                  {project.business_units && project.business_units.length > 0 && (
                      <Box mt={2}>
                          <Typography variant="caption" fontWeight={700} sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>BUSINESS UNITS</Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {project.business_units.map((unit, index) => (
                              <Box key={index} sx={{ px: 1.5, py: 0.5, borderRadius: 10, fontSize: '0.75rem', fontWeight: 600, bgcolor: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc', border: '1px solid rgba(99, 102, 241, 0.2)' }}>{unit}</Box>
                            ))}
                          </Stack>
                      </Box>
                  )}
                </Box>
              </Stack>
            </Grid>

            {/* COLUMNA DERECHA */}
            <Grid item xs={12} lg={4}>
              <Stack spacing={{ xs: 1.5, md: 3 }}>
                <Box sx={glassCardStyle}>
                  <Typography variant="subtitle2" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, mb: 3 }}>Key Details</Typography>
                  <Stack spacing={2.5}>
                    <Box sx={detailRowStyle}><Typography variant="body2" color="#94a3b8">Start Date</Typography><Typography variant="body2" fontWeight={500} color="#f8fafc">{formatDate(project.start_date)}</Typography></Box>
                    <Box sx={detailRowStyle}><Typography variant="body2" color="#94a3b8">Delivery</Typography><Typography variant="body2" fontWeight={500} color="#f8fafc">{formatDate(project.end_date)}</Typography></Box>
                    <Box sx={detailRowStyle}>
                      <Typography variant="body2" color="#94a3b8">Budget (Current PO)</Typography>
                      <Typography variant="body2" fontWeight={700} color="#34d399">{formatCurrency(financeStats.budget)}</Typography>
                    </Box>
                    <Box sx={detailRowStyle}>
                      <Typography variant="body2" color="#94a3b8">Intercompany</Typography>
                      <Typography variant="body2" fontWeight={700} color={project.intercompany_sale_enabled ? '#c084fc' : '#94a3b8'}>
                        {project.intercompany_sale_enabled ? 'Active' : 'Not applied'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={glassCardStyle}>
                  <Typography variant="subtitle2" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, mb: 3 }}>Client</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, minWidth: 0, '& svg': { flexShrink: 0 } }}><Building2 size={18} color="#64748b" /><Typography variant="body2" fontWeight={600} color="#f8fafc" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{project.company_name}</Typography></Box>
                  <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0, '& svg': { flexShrink: 0 } }}><User size={18} color="#64748b" /><Typography variant="body2" color="#cbd5e1" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{project.contact_person || 'No contact'}</Typography></Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* 🔹 PESTAÑA 2: FINANZAS DETALLADAS (NUEVA) */}
		      {currentTab === 2 && (
            !workspacePermissions.finance && !workspacePermissions.billingDocuments ? (
              <Alert severity="info">
                Your current role does not have permission to view financial or billing information for this project.
              </Alert>
            ) : (
		        <Box sx={{ ...mobileSectionStyle, bgcolor: '#0f172a', color: '#e2e8f0' }}>
	            {/* Tarjetas de Resumen */}
	            <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ ...glassCardStyle, borderLeft: '4px solid #3b82f6' }}>
                        <Typography color="#94a3b8" variant="caption" fontWeight={700}>TOTAL BUDGET</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ my: 1, color: '#60a5fa', fontSize: { xs: '1.65rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>{formatCurrency(financeStats.budget)}</Typography>
                        <Typography variant="body2" color="#cbd5e1">
                          {workspaceSummary.current_po_number
                            ? `Current PO: ${workspaceSummary.current_po_number}`
                            : 'No PO linked yet'}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ ...glassCardStyle, borderLeft: '4px solid #ef4444' }}>
                        <Typography color="#94a3b8" variant="caption" fontWeight={700}>TOTAL BILLED</Typography>
                        <Typography variant="h4" fontWeight={700} sx={{ my: 1, color: '#86efac', fontSize: { xs: '1.65rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>{formatCurrency(financeStats.billed)}</Typography>
                        <Typography variant="body2" color="#cbd5e1" sx={{ mb: 1 }}>
                          {workspaceSummary.billing_documents_total} documents linked (INV/Quote/PO)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={financeStats.percent} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: financeStats.percent > 90 ? '#ef4444' : '#22c55e' } }} />
                            <Typography variant="caption">{financeStats.percent.toFixed(0)}%</Typography>
                        </Box>
                    </Box>
                </Grid>
	                <Grid item xs={12} md={4}>
	                    <Box sx={{ ...glassCardStyle, borderLeft: `4px solid ${financeStats.remaining >= 0 ? '#3b82f6' : '#ef4444'}` }}>
	                        <Typography color="#94a3b8" variant="caption" fontWeight={700}>BUDGET BALANCE</Typography>
	                        <Typography variant="h4" fontWeight={700} sx={{ my: 1, color: financeStats.remaining >= 0 ? '#60a5fa' : '#fda4af', fontSize: { xs: '1.65rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>
	                            {formatCurrency(financeStats.remaining)}
	                        </Typography>
	                        <Typography variant="body2" color="#cbd5e1">
	                            {financeStats.remaining >= 0
                                ? 'Budget remaining after billed documents'
                                : 'Billed amount is over the current budget'}
	                        </Typography>
	                    </Box>
	                </Grid>
	                {workspacePermissions.billingDocuments && (
	                  <>
	                    <Grid item xs={12} md={6}>
	                      <Box sx={{ ...glassCardStyle, borderLeft: '4px solid #22c55e' }}>
	                        <Typography color="#94a3b8" variant="caption" fontWeight={700}>PAID TOTAL</Typography>
	                        <Typography variant="h4" fontWeight={700} sx={{ my: 1, color: '#86efac', fontSize: { xs: '1.65rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>
	                          {formatCurrency(financeStats.paid)}
	                        </Typography>
	                        <Typography variant="body2" color="#cbd5e1">
	                          {workspaceSummary.invoices_total} invoices linked for cash flow tracking
	                        </Typography>
	                      </Box>
	                    </Grid>
	                    <Grid item xs={12} md={6}>
	                      <Box sx={{ ...glassCardStyle, borderLeft: '4px solid #a855f7' }}>
	                        <Typography color="#94a3b8" variant="caption" fontWeight={700}>PROFIT (INVOICES)</Typography>
	                        <Typography variant="h4" fontWeight={700} sx={{ my: 1, color: '#c084fc', fontSize: { xs: '1.65rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>
	                          {formatCurrency(financeStats.profit)}
	                        </Typography>
	                        <Typography variant="body2" color="#cbd5e1">
	                          Income generated from project invoices
	                        </Typography>
	                      </Box>
	                    </Grid>
	                  </>
	                )}
	            </Grid>

            {/* Tabla de Gastos */}
            <Box sx={glassCardStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 }, minWidth: 0 }}>
                    <Box sx={iconBoxStyle('rgba(239, 68, 68, 0.15)', '#f87171')}><ReceiptLong /></Box>
                    <Typography variant="h6" fontWeight={700} color="#f8fafc" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>Project Expenses</Typography>
                </Box>

                {project.intercompany_sale_enabled && (
                  <Box sx={{ mb: 4 }}>
                    <ProjectFiles
                      projectId={project.id}
                      files={intercompanyInvoiceFiles}
                      onFileChange={loadData}
                      attachmentCategory="intercompany_invoice"
                      title="Intercompany Invoice Support"
                      description="Sube aqui la invoice interna o soporte de compra entre SMTLINK LLC y SMTLINK SA de CV para este proyecto."
                      buttonLabel="Subir Invoice Interna"
                      helperText="Soporta PDF, XML o imagenes del documento interno (Max 10MB)"
                      accept=".pdf,.xml,.jpg,.jpeg,.png"
                    />
                  </Box>
                )}

                {expenses.length === 0 ? (
                    <Box textAlign="center" py={4} color="#64748b">
                        <Typography>No expenses recorded for this project yet.</Typography>
                    </Box>
                ) : (
                    <>
                    <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                      {expenses.map((expense) => (
                        <Paper key={`mobile-expense-${expense.id}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2.2, bgcolor: 'rgba(15,23,42,0.42)', borderColor: 'rgba(148,163,184,0.18)' }}>
                          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={800} color="#f8fafc" sx={{ overflowWrap: 'anywhere' }}>{expense.title || expense.category}</Typography>
                              <Typography variant="caption" color="#94a3b8">{formatDate(expense.date)}</Typography>
                            </Box>
                            <Typography fontWeight={800} color="#fda4af" sx={{ flexShrink: 0 }}>{formatCurrency(expense.amount)}</Typography>
                          </Stack>
                          {expense.description && <Typography variant="body2" color="#cbd5e1" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{expense.description}</Typography>}
                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mt: 1.2 }}>
                            <Chip size="small" label={expense.category} sx={{ maxWidth: '75%', bgcolor: `${EXPENSE_CATEGORIES[expense.category] || '#94a3b8'}20`, color: EXPENSE_CATEGORIES[expense.category] || '#e2e8f0' }} />
                            {expense.receipt_path && <Button size="small" variant="outlined" onClick={() => handleOpenStoredPath(expense.receipt_path)}>Receipt</Button>}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                    <TableContainer sx={{ ...scrollTableStyle, display: { xs: 'none', md: 'block' } }}>
                        <Table sx={{ minWidth: 720, '& th, & td': { borderColor: 'rgba(148, 163, 184, 0.1)', color: '#e2e8f0' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>DATE</TableCell>
                                    <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>CATEGORY</TableCell>
                                    <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>DESCRIPTION</TableCell>
                                    <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="right">AMOUNT</TableCell>
                                    <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="center">RECEIPT</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {expenses.map((expense) => (
                                    <TableRow key={expense.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                        <TableCell>{formatDate(expense.date)}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={expense.category} 
                                                size="small" 
                                                sx={{ 
                                                    bgcolor: `${EXPENSE_CATEGORIES[expense.category] || '#94a3b8'}20`, 
                                                    color: EXPENSE_CATEGORIES[expense.category] || '#e2e8f0',
                                                    border: `1px solid ${EXPENSE_CATEGORIES[expense.category] || '#94a3b8'}40`,
                                                    fontWeight: 600
                                                }} 
                                            />
                                        </TableCell>
                                        <TableCell>{expense.title} {expense.description && `- ${expense.description}`}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                            {formatCurrency(expense.amount)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {expense.receipt_path ? (
                                                <Tooltip title="View Receipt">
	                                            <IconButton 
	                                                        size="small" 
	                                                        sx={{ color: '#a78bfa' }}
	                                                        onClick={() => handleOpenStoredPath(expense.receipt_path)}
	                                                    >
	                                                        <FileText size={18} />
	                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="#64748b">-</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
	                </>
	                )}
	            </Box>

	            <Box sx={{ ...glassCardStyle, mt: 3 }}>
	              <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 }, minWidth: 0 }}>
	                <Box sx={iconBoxStyle('rgba(34, 197, 94, 0.15)', '#86efac')}>
	                  <Description />
	                </Box>
	                <Typography variant="h6" fontWeight={700} color="#f8fafc" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
	                  Billing Documents Linked To Project
	                </Typography>
	              </Box>

	              {!workspacePermissions.billingDocuments ? (
	                <Alert severity="info" sx={{ bgcolor: 'rgba(56, 189, 248, 0.12)', color: '#e2e8f0' }}>
	                  Your current role does not have permission to view billing documents for this project.
	                </Alert>
	              ) : linkedInvoices.length === 0 ? (
	                <Box textAlign="center" py={4} color="#64748b">
	                  <Typography>No billing documents linked to this project yet.</Typography>
	                </Box>
	              ) : (
	                <>
	                <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' } }}>
	                  {linkedInvoices.map((invoice) => (
	                    <Paper key={`mobile-linked-invoice-${invoice.id}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2.2, bgcolor: 'rgba(15,23,42,0.42)', borderColor: 'rgba(148,163,184,0.18)' }}>
	                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
	                        <Box sx={{ minWidth: 0 }}>
	                          <Typography fontWeight={800} color="#f8fafc" sx={{ overflowWrap: 'anywhere' }}>{invoice.invoice_number || `DOC-${invoice.id}`}</Typography>
	                          <Typography variant="caption" color="#94a3b8">{formatDate(invoice.issue_date)}</Typography>
	                        </Box>
	                        <Typography fontWeight={800} color="#86efac" sx={{ flexShrink: 0 }}>{formatCurrency(invoice.total)}</Typography>
	                      </Stack>
	                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.2 }}>
	                        <Chip size="small" label={(invoice.type || 'invoice').replace(/_/g, ' ')} />
	                        <Chip size="small" color={String(invoice.status || '').toLowerCase() === 'paid' ? 'success' : 'warning'} label={invoice.status || '-'} />
	                        {invoice.file_path && <Button size="small" variant="outlined" onClick={() => handleOpenStoredPath(invoice.file_path)} sx={{ ml: 'auto' }}>Open</Button>}
	                      </Stack>
	                    </Paper>
	                  ))}
	                </Stack>
	                <TableContainer sx={{ ...scrollTableStyle, display: { xs: 'none', md: 'block' } }}>
	                  <Table sx={{ minWidth: 760, '& th, & td': { borderColor: 'rgba(148, 163, 184, 0.1)', color: '#e2e8f0' } }}>
	                    <TableHead>
	                      <TableRow>
	                        <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>FOLIO</TableCell>
	                        <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>TYPE</TableCell>
	                        <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>STATUS</TableCell>
	                        <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>ISSUE</TableCell>
	                        <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="right">TOTAL</TableCell>
	                        <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="center">FILE</TableCell>
	                      </TableRow>
	                    </TableHead>
	                    <TableBody>
	                      {linkedInvoices.map((invoice) => (
	                        <TableRow key={invoice.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
	                          <TableCell sx={{ fontWeight: 700 }}>{invoice.invoice_number || `DOC-${invoice.id}`}</TableCell>
	                          <TableCell>
	                            <Chip size="small" label={(invoice.type || 'invoice').replace(/_/g, ' ')} />
	                          </TableCell>
	                          <TableCell>
	                            <Chip size="small" color={String(invoice.status || '').toLowerCase() === 'paid' ? 'success' : 'warning'} label={invoice.status || '-'} />
	                          </TableCell>
	                          <TableCell>{formatDate(invoice.issue_date)}</TableCell>
	                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
	                            {formatCurrency(invoice.total)}
	                          </TableCell>
	                          <TableCell align="center">
	                            {invoice.file_path ? (
	                              <Button
	                                size="small"
	                                variant="outlined"
	                                onClick={() => handleOpenStoredPath(invoice.file_path)}
	                              >
	                                Open
	                              </Button>
	                            ) : (
	                              <Typography variant="caption" color="#64748b">-</Typography>
	                            )}
	                          </TableCell>
	                        </TableRow>
	                      ))}
	                    </TableBody>
	                  </Table>
	                </TableContainer>
	                </>
	              )}
	            </Box>
		        </Box>
            )
		      )}

	      {/* 🔹 PESTAÑA 3: ARCHIVOS */}
	      {currentTab === 3 && (
	        <Box sx={{ display: 'grid', width: '100%', minWidth: 0, maxWidth: '100%', gap: { xs: 1.5, md: 3 }, overflow: 'hidden' }}>
	          <ProjectFiles
              projectId={project.id}
              files={generalProjectFiles}
              onFileChange={loadData}
              attachmentCategory="general"
            />

	          <Box sx={{ ...glassCardStyle, bgcolor: '#0f172a', color: '#e2e8f0' }}>
	            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 }, minWidth: 0 }}>
	              <Box sx={iconBoxStyle('rgba(34, 197, 94, 0.15)', '#86efac')}>
	                <Description />
	              </Box>
	              <Typography variant="h6" fontWeight={700} color="#f8fafc" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
	                Billing Documents (PO / Quote / Invoice)
	              </Typography>
	            </Box>

	            {!workspacePermissions.billingDocuments ? (
	              <Alert severity="info" sx={{ bgcolor: 'rgba(56, 189, 248, 0.12)', color: '#e2e8f0' }}>
	                Your current role does not have permission to view billing documents.
	              </Alert>
	            ) : linkedInvoices.length === 0 ? (
	              <Box textAlign="center" py={4} color="#64748b">
	                <Typography>There are no purchase orders, quotations, or invoices linked to this project.</Typography>
	              </Box>
	            ) : (
	              <>
	              <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' } }}>
	                {linkedInvoices.map((invoice) => (
	                  <Paper key={`mobile-file-invoice-${invoice.id}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2.2, bgcolor: 'rgba(15,23,42,0.42)', borderColor: 'rgba(148,163,184,0.18)' }}>
	                    <Typography fontWeight={800} color="#f8fafc" sx={{ overflowWrap: 'anywhere' }}>{invoice.invoice_number || `DOC-${invoice.id}`}</Typography>
	                    <Typography variant="caption" color="#94a3b8">{formatDate(invoice.issue_date || invoice.created_at)}</Typography>
	                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
	                      <Chip size="small" label={(invoice.type || 'invoice').replace(/_/g, ' ')} />
	                      <Chip size="small" color={String(invoice.status || '').toLowerCase() === 'paid' ? 'success' : 'warning'} label={invoice.status || '-'} />
	                    </Stack>
	                    <Stack direction="row" spacing={1} sx={{ mt: 1.2 }}>
	                      {invoice.file_path && <Button fullWidth size="small" variant="outlined" onClick={() => handleOpenStoredPath(invoice.file_path)}>Attachment</Button>}
	                      <Button fullWidth size="small" variant="contained" onClick={() => handleOpenOfficialInvoicePdf(invoice.id)}>Official PDF</Button>
	                    </Stack>
	                  </Paper>
	                ))}
	              </Stack>
	              <TableContainer sx={{ ...scrollTableStyle, display: { xs: 'none', md: 'block' } }}>
	                <Table sx={{ minWidth: 800, '& th, & td': { borderColor: 'rgba(148, 163, 184, 0.1)', color: '#e2e8f0' } }}>
	                  <TableHead>
	                    <TableRow>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>FOLIO</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>TYPE</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>STATUS</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>DATE</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="center">ATTACHED FILE</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="center">OFFICIAL PDF</TableCell>
	                    </TableRow>
	                  </TableHead>
	                  <TableBody>
	                    {linkedInvoices.map((invoice) => (
	                      <TableRow key={`files-invoice-${invoice.id}`} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
	                        <TableCell sx={{ fontWeight: 700 }}>{invoice.invoice_number || `DOC-${invoice.id}`}</TableCell>
	                        <TableCell>
	                          <Chip size="small" label={(invoice.type || 'invoice').replace(/_/g, ' ')} />
	                        </TableCell>
	                        <TableCell>
	                          <Chip
	                            size="small"
	                            color={String(invoice.status || '').toLowerCase() === 'paid' ? 'success' : 'warning'}
	                            label={invoice.status || '-'}
	                          />
	                        </TableCell>
	                        <TableCell>{formatDate(invoice.issue_date || invoice.created_at)}</TableCell>
	                        <TableCell align="center">
	                          {invoice.file_path ? (
	                            <Button
	                              size="small"
	                              variant="outlined"
	                              onClick={() => handleOpenStoredPath(invoice.file_path)}
	                            >
	                              Open
	                            </Button>
	                          ) : (
	                            <Typography variant="caption" color="#64748b">-</Typography>
	                          )}
	                        </TableCell>
	                        <TableCell align="center">
	                          <Button
	                            size="small"
	                            variant="contained"
	                            onClick={() => handleOpenOfficialInvoicePdf(invoice.id)}
	                          >
	                            Open PDF
	                          </Button>
	                        </TableCell>
	                      </TableRow>
	                    ))}
	                  </TableBody>
	                </Table>
	              </TableContainer>
	              </>
	            )}
	          </Box>

	          <Box sx={{ ...glassCardStyle, bgcolor: '#0f172a', color: '#e2e8f0' }}>
	            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 }, minWidth: 0 }}>
	              <Box sx={iconBoxStyle('rgba(59, 130, 246, 0.15)', '#93c5fd')}>
	                <AttachFile />
	              </Box>
	              <Typography variant="h6" fontWeight={700} color="#f8fafc" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
	                Related Documents In This Project
	              </Typography>
	            </Box>

	            {!workspacePermissions.documents ? (
	              <Alert severity="info" sx={{ bgcolor: 'rgba(56, 189, 248, 0.12)', color: '#e2e8f0' }}>
	                Tu rol actual no tiene permiso para visualizar documentos relacionados.
	              </Alert>
	            ) : linkedDocuments.length === 0 ? (
	              <Box textAlign="center" py={4} color="#64748b">
	                <Typography>No additional documents linked to this project.</Typography>
	              </Box>
	            ) : (
	              <>
	              <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' } }}>
	                {linkedDocuments.map((doc) => (
	                  <Paper key={`mobile-related-document-${doc.id}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2.2, bgcolor: 'rgba(15,23,42,0.42)', borderColor: 'rgba(148,163,184,0.18)' }}>
	                    <Typography fontWeight={800} color="#f8fafc" sx={{ overflowWrap: 'anywhere' }}>{doc.name || `Document ${doc.id}`}</Typography>
	                    <Typography variant="caption" color="#94a3b8">{formatDate(doc.created_at)}</Typography>
	                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.2 }}>
	                      <Chip size="small" label={doc.file_type || 'File'} />
	                      <Chip size="small" variant="outlined" label={doc.category || 'Documents'} />
	                      {doc.file_path && <Button size="small" variant="outlined" onClick={() => handleOpenStoredPath(doc.file_path)} sx={{ ml: 'auto' }}>Open</Button>}
	                    </Stack>
	                  </Paper>
	                ))}
	              </Stack>
	              <TableContainer sx={{ ...scrollTableStyle, display: { xs: 'none', md: 'block' } }}>
	                <Table sx={{ minWidth: 680, '& th, & td': { borderColor: 'rgba(148, 163, 184, 0.1)', color: '#e2e8f0' } }}>
	                  <TableHead>
	                    <TableRow>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>NAME</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>TYPE</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>CATEGORY</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }}>DATE</TableCell>
	                      <TableCell sx={{ color: '#94a3b8 !important', fontWeight: 700 }} align="center">FILE</TableCell>
	                    </TableRow>
	                  </TableHead>
	                  <TableBody>
	                    {linkedDocuments.map((doc) => (
	                      <TableRow key={doc.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
	                        <TableCell sx={{ fontWeight: 600 }}>{doc.name || `Document ${doc.id}`}</TableCell>
	                        <TableCell>{doc.file_type || '-'}</TableCell>
	                        <TableCell>{doc.category || 'Documents'}</TableCell>
	                        <TableCell>{formatDate(doc.created_at)}</TableCell>
	                        <TableCell align="center">
	                          {doc.file_path ? (
	                            <Button
	                              size="small"
	                              variant="outlined"
	                              onClick={() => handleOpenStoredPath(doc.file_path)}
	                            >
	                              Open
	                            </Button>
	                          ) : (
	                            <Typography variant="caption" color="#64748b">-</Typography>
	                          )}
	                        </TableCell>
	                      </TableRow>
	                    ))}
	                  </TableBody>
	                </Table>
	              </TableContainer>
	              </>
	            )}
	          </Box>
	        </Box>
	      )}

        {/* 🔹 PESTAÑA 4: FOLLOW-UP (seguimiento diario/semanal) */}
        {currentTab === 4 && (
          <Box sx={{ ...mobileSectionStyle, bgcolor: followupPalette.pageBg, color: followupPalette.text }}>
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                mb: { xs: 1.5, md: 3 },
                p: { xs: 1.5, md: 3 },
                borderRadius: { xs: 2.5, md: 4 },
                border: followupPalette.mainBorder,
                background: followupPalette.panelBg
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={800} color={followupPalette.text} sx={{ overflowWrap: 'anywhere' }}>Project Follow-up</Typography>
                  <Typography variant="body2" color={followupPalette.subText} sx={{ overflowWrap: 'anywhere' }}>
                    Registro diario/semanal de avances para validar trabajo real del equipo.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    label={`${followupsData.summary?.total_entries || 0} Entries`}
                    sx={{ bgcolor: 'rgba(59,130,246,0.2)', color: isLightMode ? '#1e3a8a' : '#bfdbfe', fontWeight: 700 }}
                  />
                  <Chip
                    label={`${followupsData.summary?.users_count || 0} Users`}
                    sx={{ bgcolor: 'rgba(245,158,11,0.2)', color: isLightMode ? '#92400e' : '#fde68a', fontWeight: 700 }}
                  />
                </Stack>
              </Stack>
            </Box>

            <Grid container spacing={{ xs: 1.5, md: 3 }}>
              <Grid item xs={12} lg={5}>
                <Box
                  sx={{
                    ...glassCardStyle,
                    border: followupPalette.cardBorder,
                    background: followupPalette.cardBg
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, minWidth: 0 }}>
                    <Box sx={iconBoxStyle('rgba(14, 165, 233, 0.2)', '#7dd3fc')}>
                      <ChecklistRtl />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={800} color={followupPalette.text}>
                        {editingFollowupId ? 'Edit Check-in' : 'New Check-in'}
                      </Typography>
                      <Typography variant="caption" color={followupPalette.subText} sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                        Enter only the main activity. All other fields are optional.
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={7}>
                      <TextField
                        fullWidth
                        label="What did you do?"
                        value={followupForm.title}
                        onChange={(e) => setFollowupForm((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g. Shared customer update and aligned next action"
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        select
                        label="Assign next to"
                        value={followupForm.assigned_to_user_id}
                        onChange={(e) => setFollowupForm((prev) => ({ ...prev, assigned_to_user_id: e.target.value }))}
                        helperText="The assigned user will receive a notification"
                      >
                        <MenuItem value="">No assignee</MenuItem>
                        {followupAssignableUsers.map((user) => (
                          <MenuItem key={user.id} value={String(user.id)}>
                            {user.name || user.username || user.email}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                        {FOLLOWUP_QUICK_TEMPLATES.map((template) => (
                          <Chip
                            key={template}
                            label={template}
                            variant="outlined"
                            onClick={() => setFollowupForm((prev) => ({ ...prev, title: template }))}
                          />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                        {FOLLOWUP_STATUSES.map((item) => (
                          <Chip
                            key={item.value}
                            label={item.label}
                            color={followupForm.status === item.value ? 'primary' : 'default'}
                            variant={followupForm.status === item.value ? 'filled' : 'outlined'}
                            onClick={() => setFollowupForm((prev) => ({ ...prev, status: item.value }))}
                          />
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        size="small"
                        variant="text"
                        endIcon={showFollowupAdvancedFields ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setShowFollowupAdvancedFields((prev) => !prev)}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        {showFollowupAdvancedFields ? 'Hide optional fields' : 'Show optional fields'}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Collapse in={showFollowupAdvancedFields}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Date"
                              type="date"
                              value={followupForm.entry_date}
                              onChange={(e) => setFollowupForm((prev) => ({ ...prev, entry_date: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              fullWidth
                              label="Status"
                              value={followupForm.status}
                              onChange={(e) => setFollowupForm((prev) => ({ ...prev, status: e.target.value }))}
                            >
                              {FOLLOWUP_STATUSES.map((item) => (
                                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              label="Details / follow-up notes"
                              value={followupForm.details}
                              onChange={(e) => setFollowupForm((prev) => ({ ...prev, details: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Next step"
                              value={followupForm.next_step}
                              onChange={(e) => setFollowupForm((prev) => ({ ...prev, next_step: e.target.value }))}
                            />
                          </Grid>
                        </Grid>
                      </Collapse>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button
                          variant="contained"
                          startIcon={editingFollowupId ? <Edit /> : <Save />}
                          onClick={handleFollowupSave}
                          disabled={followupSaving}
                          sx={{
                            fontWeight: 700,
                            px: 2.5,
                            background: 'linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)'
                          }}
                        >
                          {editingFollowupId ? 'Update check-in' : 'Save check-in'}
                        </Button>
                        {editingFollowupId && (
                          <Button variant="outlined" onClick={resetFollowupForm}>Cancel edit</Button>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} lg={7}>
                <Box
                  sx={{
                    ...glassCardStyle,
                    border: followupPalette.cardBorder,
                    background: followupPalette.cardBg
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={followupFilters.group_by}
                      onChange={async (_, val) => {
                        if (!val) return;
                        await handleFollowupFilterChange('group_by', val);
                      }}
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        '& .MuiToggleButton-root': {
                          flex: { xs: 1, md: 'initial' },
                          color: isLightMode ? '#334155' : '#cbd5e1',
                          borderColor: 'rgba(148,163,184,0.25)'
                        },
                        '& .Mui-selected': {
                          color: isLightMode ? '#0f172a' : '#f8fafc',
                          bgcolor: 'rgba(59,130,246,0.35) !important'
                        }
                      }}
                    >
                      <ToggleButton value="day">By Day</ToggleButton>
                      <ToggleButton value="week">By Week</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      value={followupFilters.from}
                      onChange={async (e) => handleFollowupFilterChange('from', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      value={followupFilters.to}
                      onChange={async (e) => handleFollowupFilterChange('to', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    {followupsData.scope === 'all' && (
                      <TextField
                        size="small"
                        select
                        label="User"
                        value={followupFilters.user_id}
                        onChange={async (e) => handleFollowupFilterChange('user_id', e.target.value)}
                        sx={{ minWidth: { xs: 0, md: 170 }, width: { xs: '100%', md: 'auto' } }}
                      >
                        <MenuItem value="all">All users</MenuItem>
                        {Array.from(new Map(
                          (followupsData.items || []).map((item) => [item.user_id, item.user_name])
                        ).entries()).map(([id, name]) => (
                          <MenuItem key={id} value={id}>{name}</MenuItem>
                        ))}
                      </TextField>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleExportFollowupsExcel}
                      sx={{
                        ml: { xs: 0, md: 'auto' },
                        borderColor: isLightMode ? 'rgba(37,99,235,0.4)' : 'rgba(125,211,252,0.35)',
                        color: isLightMode ? '#1d4ed8' : '#bae6fd',
                        fontWeight: 700
                      }}
                    >
                      Export Excel
                    </Button>
                  </Stack>

                  {followupsError && <Alert severity="error" sx={{ mb: 2 }}>{followupsError}</Alert>}

                  <Stack spacing={1.1} sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}>
                    {followupUserSummary.length === 0 ? (
                      <Typography variant="caption" color={followupPalette.subText}>No summary data in selected range.</Typography>
                    ) : followupUserSummary.map((row) => (
                      <Paper
                        key={`mobile-fu-summary-${row.user_id || row.user_name}`}
                        variant="outlined"
                        onClick={async () => {
                          if (followupsData.scope !== 'all' || !row.user_id) return;
                          await handleFollowupFilterChange('user_id', String(row.user_id));
                        }}
                        sx={{ p: 1.35, borderRadius: 2.2, bgcolor: followupPalette.tableBg, border: followupPalette.tableBorder, cursor: followupsData.scope === 'all' && row.user_id ? 'pointer' : 'default' }}
                      >
                        <Typography variant="subtitle2" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>{row.user_name}</Typography>
                        {row.user_email && <Typography variant="caption" color={followupPalette.subText} sx={{ display: 'block', overflowWrap: 'anywhere' }}>{row.user_email}</Typography>}
                        <Box sx={{ mt: 1.1, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.6, textAlign: 'center' }}>
                          {[['Entries', row.entries], ['Assigned', row.assigned_count || 0], ['Done', row.done_count], ['Blocked', row.blocked_count]].map(([label, value]) => (
                            <Box key={label} sx={{ minWidth: 0 }}><Typography variant="caption" color={followupPalette.subText}>{label}</Typography><Typography variant="body2" fontWeight={850}>{value}</Typography></Box>
                          ))}
                        </Box>
                        <Typography variant="caption" color={followupPalette.subText} sx={{ display: 'block', mt: 1 }}>Latest: {row.latest_entry_date || '-'}</Typography>
                      </Paper>
                    ))}
                  </Stack>

                  <TableContainer
                    sx={{
                      ...scrollTableStyle,
                      display: { xs: 'none', md: 'block' },
                      mb: 2,
                      borderRadius: 2,
                      border: followupPalette.tableBorder,
                      bgcolor: followupPalette.tableBg
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        minWidth: 680,
                        '& th, & td': {
                          borderColor: 'rgba(148,163,184,0.12)',
                          color: isLightMode ? '#1e293b' : '#e2e8f0'
                        }
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: `${isLightMode ? '#1d4ed8' : '#93c5fd'} !important`, fontWeight: 800 }}>User</TableCell>
                          <TableCell sx={{ color: `${isLightMode ? '#1d4ed8' : '#93c5fd'} !important`, fontWeight: 800 }} align="right">Entries</TableCell>
                          <TableCell sx={{ color: `${isLightMode ? '#1d4ed8' : '#93c5fd'} !important`, fontWeight: 800 }} align="right">Assigned</TableCell>
                          <TableCell sx={{ color: `${isLightMode ? '#1d4ed8' : '#93c5fd'} !important`, fontWeight: 800 }} align="right">Done</TableCell>
                          <TableCell sx={{ color: `${isLightMode ? '#1d4ed8' : '#93c5fd'} !important`, fontWeight: 800 }} align="right">Blocked</TableCell>
                          <TableCell sx={{ color: `${isLightMode ? '#1d4ed8' : '#93c5fd'} !important`, fontWeight: 800 }}>Latest</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {followupUserSummary.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography variant="caption" color={followupPalette.subText}>No summary data in selected range.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : followupUserSummary.map((row) => (
                          <TableRow
                            key={`fu-summary-${row.user_id || row.user_name}`}
                            hover
                            onClick={async () => {
                              if (followupsData.scope !== 'all' || !row.user_id) return;
                              await handleFollowupFilterChange('user_id', String(row.user_id));
                            }}
                            sx={{
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                              cursor: followupsData.scope === 'all' && row.user_id ? 'pointer' : 'default'
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>{row.user_name}</Typography>
                              {row.user_email ? (
                                <Typography variant="caption" color={followupPalette.subText}>{row.user_email}</Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right">{row.entries}</TableCell>
                            <TableCell align="right">{row.assigned_count || 0}</TableCell>
                            <TableCell align="right">{row.done_count}</TableCell>
                            <TableCell align="right">{row.blocked_count}</TableCell>
                            <TableCell>{row.latest_entry_date || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {followupsLoading ? (
                    <Box display="flex" justifyContent="center" py={5}><CircularProgress size={30} /></Box>
                  ) : (followupsData.groups || []).length === 0 ? (
                    <Alert severity="info" sx={{ border: isLightMode ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(56,189,248,0.25)' }}>
                      No follow-up activity found in this range.
                    </Alert>
                  ) : (
                    <Stack spacing={2}>
                      {(followupsData.groups || []).map((group) => (
                        <Box
                          key={group.key}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.22)',
                            background: 'linear-gradient(180deg, rgba(30,41,59,0.56) 0%, rgba(15,23,42,0.56) 100%)'
                          }}
                        >
                          <Stack direction="row" useFlexGap flexWrap="wrap" justifyContent="space-between" alignItems="center" mb={1.2}>
                            <Typography variant="subtitle2" color="#e2e8f0" fontWeight={800} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{group.label}</Typography>
                              <Chip
                                size="small"
                                label={`${group.count} entries`}
                                sx={{ bgcolor: 'rgba(148,163,184,0.2)', color: '#e2e8f0' }}
                              />
                          </Stack>

                          <Stack spacing={1.2}>
                            {group.items.map((item) => {
                              const statusMeta = getFollowupStatusMeta(item.status);
                              return (
                                <Box
                                  key={item.id}
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: '1px solid rgba(148,163,184,0.18)',
                                    bgcolor: 'rgba(15,23,42,0.48)'
                                  }}
                                >
                                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Stack direction="row" spacing={1} useFlexGap alignItems="center" mb={0.5} flexWrap="wrap">
                                        <Chip
                                          size="small"
                                          label={statusMeta.label}
                                          sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700 }}
                                        />
                                        <Typography variant="caption" color="#94a3b8">{item.user_name}</Typography>
                                      </Stack>
                                      <Typography variant="body2" fontWeight={700} color="#f8fafc" sx={{ overflowWrap: 'anywhere' }}>{item.title}</Typography>
                                      {item.details ? (
                                        <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mt: 0.3, overflowWrap: 'anywhere' }}>
                                          {item.details}
                                        </Typography>
                                      ) : null}
                                      {item.next_step ? (
                                        <Typography variant="caption" color="#7dd3fc" sx={{ display: 'block', mt: 0.3, overflowWrap: 'anywhere' }}>
                                          Next: {item.next_step}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                    <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.5}>
                                      {item.assigned_to_user_name ? (
                                        <Typography variant="caption" color="#cbd5e1">Assigned: {item.assigned_to_user_name}</Typography>
                                      ) : (
                                        <Typography variant="caption" color="#94a3b8">No assignee</Typography>
                                      )}
                                      <Stack direction="row" spacing={0.5}>
                                        <IconButton size="small" onClick={() => handleFollowupEdit(item)} sx={{ color: '#bfdbfe' }}>
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleFollowupDelete(item)} sx={{ color: '#fca5a5' }}>
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
    </Container>
  );
}
